import { useAuth, useOrganization } from '@clerk/clerk-react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { ingestionKeys } from '@/lib/queryKeys'
import { getIngestionRunFacets, getIngestionRuns } from '../api'
import type {
  IngestionRunFacets,
  IngestionRunsQueryParams,
  IngestionRunsResponse,
} from '../types'

export function useIngestionRunsQuery(params: IngestionRunsQueryParams = {}) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery<IngestionRunsResponse>({
    queryKey: ingestionKeys.runs(organization?.id, params),
    enabled: !!organization?.id,
    queryFn: async ({ pageParam, signal }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return getIngestionRuns(
        token,
        params,
        pageParam as string | undefined,
        signal
      )
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages
      // Infinite-query refetches reload every cached page. Stop automatic
      // polling once the user has loaded older history.
      if (!pages || pages.length !== 1) return false
      const runs = pages[0]?.data ?? []
      if (
        runs.some(
          (run) => run.status === 'queued' || run.status === 'dispatching'
        )
      )
        return 2_000
      return runs.some((run) => run.status === 'running') ? 10_000 : false
    },
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useIngestionRunFacetsQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery<IngestionRunFacets>({
    queryKey: ingestionKeys.facets(organization?.id),
    enabled: !!organization?.id,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return getIngestionRunFacets(token, signal)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
